import React, { useEffect, useState } from "react";
import {
    Paper,
    Skeleton,
    Typography,
    Box,
    Divider,
    Button,
    Grid,
    Card,
    CardContent,
    Chip,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
} from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DescriptionIcon from '@mui/icons-material/Description';
import BusinessIcon from '@mui/icons-material/Business';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import GavelIcon from '@mui/icons-material/Gavel';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import DownloadIcon from '@mui/icons-material/Download';
import VerifiedIcon from '@mui/icons-material/Verified';
import { useBiddingDetailController } from "./context/bidding-detail-controller";
import { useBiddingDetailState } from "./context/bidding-detail-context";
import PageHeader from "../components/shared/page-header";
import { useParams } from "react-router-dom";
import { useNavigate } from "../../hooks/useNavigate";
import dayjs from "dayjs";

export function BiddingDetailComponent() {
    const [initialLoading, setInitialLoading] = useState(true);
    const { loadBiddingDetail } = useBiddingDetailController();
    const { biddingDetail } = useBiddingDetailState();
    const { id } = useParams();
    const navigate = useNavigate();

    useEffect(() => {
        const fetchData = async () => {
            if (id) {
                await loadBiddingDetail(Number(id));
                setInitialLoading(false);
            }
        };
        fetchData();
    }, [id]);

    useEffect(() => {
        if (biddingDetail?.externalUrl && !initialLoading) {
            window.open(biddingDetail.externalUrl, '_blank', 'noopener,noreferrer');
            navigate('/licitacoes');
        }
    }, [biddingDetail, initialLoading, navigate]);

    const handleBackToBidding = () => {
        navigate('/licitacoes');
    };

    const getStatusColor = (status?: number) => {
        switch (status) {
            case 1: return 'info'; // Em andamento
            case 2: return 'success'; // Concluída
            case 3: return 'warning'; // Suspensa
            case 4: return 'error'; // Cancelada
            default: return 'default';
        }
    };

    const handleViewDocument = (doc: any) => {
        if (doc?.fileViewUrl) {
            window.open(doc.fileViewUrl, '_blank');
        }
    };

    const handleDownloadDocument = (doc: any) => {
        if (doc?.fileDownloadUrl) {
            window.open(doc.fileDownloadUrl, '_blank');
        }
    };

    return (
        <Box>
            <PageHeader
                title={biddingDetail?.number || 'Carregando...'}
                subtitle="Detalhes da Licitação"
                breadcrumbs={[
                    { label: 'Licitações', href: '/licitacoes' },
                    { label: biddingDetail?.number || 'Carregando...' }
                ]}
            />

            {initialLoading ? (
                <Box>
                    <Skeleton variant="rounded" width="100%" height={200} sx={{ borderRadius: 2, mb: 3 }} />
                    <Skeleton variant="text" width="60%" height={60} sx={{ mb: 2 }} />
                    <Skeleton variant="text" width="100%" height={30} />
                    <Skeleton variant="text" width="100%" height={30} />
                    <Skeleton variant="text" width="80%" height={30} />
                </Box>
            ) : (
                <React.Fragment>
                    <Paper sx={{ p: 4, mb: 3 }} elevation={2}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                            <Typography variant="h5" component="h1" fontWeight={600}>
                                {biddingDetail?.number}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                {biddingDetail?.isPncp && (
                                    <Chip
                                        icon={<VerifiedIcon />}
                                        label="PNCP"
                                        color="success"
                                        size="medium"
                                    />
                                )}
                                {biddingDetail?.status !== undefined && (
                                    <Chip
                                        label={biddingDetail.statusDescripton || 'Status desconhecido'}
                                        color={getStatusColor(biddingDetail.status)}
                                        size="medium"
                                    />
                                )}
                            </Box>
                        </Box>

                        <Divider sx={{ my: 3 }} />

                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2 }}>
                                            <DescriptionIcon color="primary" />
                                            <Box>
                                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                    Objeto
                                                </Typography>
                                                <Typography variant="body1">
                                                    {biddingDetail?.object || 'Não informado'}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                            <BusinessIcon color="primary" />
                                            <Box>
                                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                    Unidade Gestora
                                                </Typography>
                                                <Typography variant="body1">
                                                    {biddingDetail?.managementUnit || 'Não informado'}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                            <GavelIcon color="primary" />
                                            <Box>
                                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                    Modalidade
                                                </Typography>
                                                <Typography variant="body1">
                                                    {biddingDetail?.modality || 'Não informado'}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>

                            {biddingDetail?.value && (
                                <Grid item xs={12} md={6}>
                                    <Card variant="outlined" sx={{ backgroundColor: 'success.light', color: 'success.contrastText' }}>
                                        <CardContent>
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                                <AttachMoneyIcon />
                                                <Box>
                                                    <Typography variant="subtitle2" gutterBottom>
                                                        Valor
                                                    </Typography>
                                                    <Typography variant="h6" fontWeight={600}>
                                                        {biddingDetail.formattedValue || `R$ ${biddingDetail.value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            )}

                            {biddingDetail?.processNumber && (
                                <Grid item xs={12} md={6}>
                                    <Card variant="outlined">
                                        <CardContent>
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                                <FolderOpenIcon color="primary" />
                                                <Box>
                                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                        Número do Processo
                                                    </Typography>
                                                    <Typography variant="body1">
                                                        {biddingDetail.processNumber}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            )}
                        </Grid>

                        <Divider sx={{ my: 3 }} />

                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CalendarTodayIcon />
                            Datas Importantes
                        </Typography>

                        <Grid container spacing={2} sx={{ mt: 1 }}>
                            {biddingDetail?.publicatedOn && (
                                <Grid item xs={12} sm={4}>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">
                                            Publicação
                                        </Typography>
                                        <Typography variant="body1" fontWeight={500}>
                                            {dayjs(biddingDetail.publicatedOn).format('DD/MM/YYYY')}
                                        </Typography>
                                    </Box>
                                </Grid>
                            )}
                            {biddingDetail?.openedOn && (
                                <Grid item xs={12} sm={4}>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">
                                            Abertura
                                        </Typography>
                                        <Typography variant="body1" fontWeight={500}>
                                            {dayjs(biddingDetail.openedOn).format('DD/MM/YYYY')}
                                        </Typography>
                                    </Box>
                                </Grid>
                            )}
                            {biddingDetail?.limitDate && (
                                <Grid item xs={12} sm={4}>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">
                                            Data Limite
                                        </Typography>
                                        <Typography variant="body1" fontWeight={500}>
                                            {dayjs(biddingDetail.limitDate).format('DD/MM/YYYY')}
                                        </Typography>
                                    </Box>
                                </Grid>
                            )}
                        </Grid>

                        {biddingDetail?.documents && biddingDetail.documents.length > 0 && (
                            <>
                                <Divider sx={{ my: 3 }} />

                                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <DescriptionIcon />
                                    Documentos ({biddingDetail.documents.length})
                                </Typography>

                                <List>
                                    {biddingDetail.documents.map((doc) => (
                                        <ListItem
                                            key={doc.id}
                                            sx={{
                                                border: '1px solid',
                                                borderColor: 'divider',
                                                borderRadius: 1,
                                                mb: 1,
                                                '&:hover': {
                                                    backgroundColor: 'action.hover',
                                                }
                                            }}
                                        >
                                            <ListItemIcon>
                                                <DescriptionIcon color="primary" />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={doc.documentType}
                                            />
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                {doc.fileViewUrl && (
                                                    <Button
                                                        variant="outlined"
                                                        startIcon={<DescriptionIcon />}
                                                        onClick={() => handleViewDocument(doc)}
                                                        size="small"
                                                    >
                                                        Visualizar
                                                    </Button>
                                                )}
                                                {doc.fileDownloadUrl && (
                                                    <Button
                                                        variant="outlined"
                                                        startIcon={<DownloadIcon />}
                                                        onClick={() => handleDownloadDocument(doc)}
                                                        size="small"
                                                    >
                                                        Baixar
                                                    </Button>
                                                )}
                                            </Box>
                                        </ListItem>
                                    ))}
                                </List>
                            </>
                        )}

                        <Divider sx={{ my: 4 }} />

                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <Button
                                variant="contained"
                                startIcon={<ArrowBackIcon />}
                                onClick={handleBackToBidding}
                                size="large"
                            >
                                Voltar para licitações
                            </Button>
                        </Box>
                    </Paper>
                </React.Fragment>
            )}
        </Box>
    );
}
